if (!customElements.get('ct-cards')) {
  customElements.define(
    'ct-cards',
    class CTCards extends HTMLElement {
      constructor() {
        super();
        this.cards = [];
        this.currentFrontCard = null;
        this.isAnimating = false;
        this.animationInterval = null;
        this.pauseTimeout = null;
        this.cardWidth = 200;
        this.cardHeight = 300;
        this.cardIndex = 0; // For cycling through cards in sequence
        this.speed = 1; // 1 = normal; 0.25 = quarter-speed
        this.debug = false;
        this.debugEl = null;
        this.debugState = { animation: 'init', stage: 'init' };
        this.coverOverlapThreshold = 0.18;
        this.headerEl = null;
        this.headerFaded = false;
      }

      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('.ct-card'));
        this.headerEl = this.querySelector('.ct-cards__header');
        this.initializeFlagsFromQuery();
        if (this.debug) this.createDebugOverlay();
        this.updateCardDimensions();
        this.initializeCards();
        this.addClickListeners();
        this.animateCards();

        // Resize handling
        this._onResize = this.handleResize.bind(this);
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      disconnectedCallback() {
        clearInterval(this.animationInterval);
        clearTimeout(this.pauseTimeout);
        if (this._onResize) {
          window.removeEventListener('resize', this._onResize);
        }
      }

      initializeCards() {
        // Ensure the container is positioned relative
        this.style.position = 'relative';

        this.cards.forEach((card, index) => {
          // Ensure each card is absolutely positioned
          card.style.position = 'absolute';

          gsap.set(card, {
            x: Math.random() * (this.offsetWidth - this.cardWidth),
            y: Math.random() * (this.offsetHeight - this.cardHeight),
            rotation: this.getRandomRotation(),
            rotationY: 180, // All cards start face down
            zIndex: index
          });
        });

        // Flip the first card to front
        this.flipNewCardToFront();
      }

      getRandomRotation() {
        return Math.random() * 60 - 30;
      }

      flipNewCardToFront(incrementIndex = true) {
        this.setDebug('flip', 'start');
        const centerX = Math.round((this.offsetWidth - this.cardWidth) / 2);
        const centerY = Math.round((this.offsetHeight - this.cardHeight) / 2);

        const previousFrontCard = this.currentFrontCard;
        // Restore image size hints for the previously front/zoomed card
        if (previousFrontCard) {
          this.updateImageSizesForCard(previousFrontCard, this.cardWidth);
        }

        // Select next card in sequence
        this.currentFrontCard = this.cards[this.cardIndex % this.cards.length];
        if (incrementIndex) {
          this.cardIndex++;
        }

        const tl = gsap.timeline({ onComplete: () => this.setDebug('flip', 'done') });

        // Ensure z-order is correct; if upcoming card starts covered by another card,
        // delay its raise slightly to avoid a pop when it begins moving.
        const isCovered = this.isCoveredByOtherCards(this.currentFrontCard, previousFrontCard);
        const raiseAt = isCovered ? this.d(0.15) : 0;
        tl.set(this.currentFrontCard, { zIndex: 1001 }, raiseAt);
        if (previousFrontCard && previousFrontCard !== this.currentFrontCard) {
          tl.set(previousFrontCard, { zIndex: 999 }, 0);
        }

        // Animate previous front card to face down instead of jumping
        if (previousFrontCard && previousFrontCard !== this.currentFrontCard) {
          tl.to(previousFrontCard, {
            rotationY: 180,
            duration: this.d(0.5),
            ease: 'power2.inOut'
          }, 0)
            .set(previousFrontCard, { zIndex: 0, scale: 1 }, '>-0.01');
        }

        // Bring the new card to the front and face up
        tl.to(this.currentFrontCard, {
          rotationY: 0,
          x: centerX,
          y: centerY,
          rotation: 0,
          zIndex: 1001,
          scale: this.getFrontScale(),
          duration: this.d(0.5),
          ease: 'power2.inOut'
        }, 0);

        return tl;
      }

      scatterAnimation() {
        this.setDebug('scatter', 'start');
        const others = this.cards.filter((c) => c !== this.currentFrontCard);
        return gsap.to(others, {
          x: () => Math.random() * (this.offsetWidth - this.cardWidth),
          y: () => Math.random() * (this.offsetHeight - this.cardHeight),
          rotation: () => this.getRandomRotation(),
          rotationY: 180, // Ensure all scattered cards are face down
          zIndex: 0,
          scale: 1,
          duration: this.d(1),
          ease: 'power2.inOut',
          onComplete: () => this.setDebug('scatter', 'done')
        });
      }

      verticalWipeAnimation() {
        const timeline = gsap.timeline();
        const centerX = Math.round((this.offsetWidth - this.cardWidth) / 2);
        const centerY = Math.round((this.offsetHeight - this.cardHeight) / 2);

        // Move cards off the top
        timeline.to(this.cards, {
          y: -this.offsetHeight,
          duration: this.d(0.5),
          stagger: this.d(0.05),
          ease: 'power2.in'
        });

        // Reset positions and move from bottom
        timeline.set(this.cards, {
          y: this.offsetHeight,
          x: (i) =>
            i === this.cards.indexOf(this.currentFrontCard)
              ? centerX
              : Math.random() * (this.offsetWidth - this.cardWidth),
          rotation: (i) =>
            i === this.cards.indexOf(this.currentFrontCard)
              ? 0
              : this.getRandomRotation(),
          zIndex: (i) => (i === this.cards.indexOf(this.currentFrontCard) ? 1000 : 0),
          rotationY: (i) => (i === this.cards.indexOf(this.currentFrontCard) ? 0 : 180),
          scale: 1
        });
        timeline.add(() => this.setDebug('verticalWipe', 'reset'));

        // Move cards back in from the bottom
        timeline.to(this.cards, {
          y: (i) =>
            i === this.cards.indexOf(this.currentFrontCard)
              ? centerY
              : Math.random() * (this.offsetHeight - this.cardHeight),
          duration: this.d(0.5),
          stagger: this.d(0.05),
          ease: 'power2.out'
        });
        timeline.eventCallback('onStart', () => this.setDebug('verticalWipe', 'start'));
        timeline.eventCallback('onComplete', () => this.setDebug('verticalWipe', 'done'));
        return timeline;
      }

      stackAnimation() {
        const timeline = gsap.timeline();
        const centerX = Math.round((this.offsetWidth - this.cardWidth) / 2);
        const centerY = Math.round((this.offsetHeight - this.cardHeight) / 2);

        // Stack all cards under the current front card
        timeline.to(this.cards, {
          x: centerX,
          y: centerY,
          rotation: 0,
          rotationY: 180, // All cards face down
          zIndex: (i) => (i === this.cards.indexOf(this.currentFrontCard) ? 1000 : i),
          scale: 1,
          duration: this.d(0.5),
          stagger: this.d(0.02),
          ease: 'power2.inOut'
        });

        // Pause for a moment
        timeline.to({}, { duration: this.d(0.5) });

        // Flip a new card to front without incrementing the index
        timeline.add(() => this.setDebug('stack', 'flipNewFront'));
        timeline.add(this.flipNewCardToFront(false));

        // Scatter cards
        timeline.to(this.cards, {
          x: (i, target) =>
            target === this.currentFrontCard
              ? centerX
              : Math.random() * (this.offsetWidth - this.cardWidth),
          y: (i, target) =>
            target === this.currentFrontCard
              ? centerY
              : Math.random() * (this.offsetHeight - this.cardHeight),
          rotation: (i, target) =>
            target === this.currentFrontCard ? 0 : this.getRandomRotation(),
          rotationY: (i, target) => (target === this.currentFrontCard ? 0 : 180),
          zIndex: (i, target) => (target === this.currentFrontCard ? 1000 : 0),
          scale: (i, target) => (target === this.currentFrontCard ? 1 : 1),
          duration: this.d(0.5),
          stagger: this.d(0.02),
          ease: 'power2.out'
        });
        timeline.add(() => this.setDebug('stack', 'scatter'));
        timeline.eventCallback('onStart', () => this.setDebug('stack', 'start'));
        timeline.eventCallback('onComplete', () => this.setDebug('stack', 'done'));
        return timeline;
      }

      addClickListeners() {
        this.cards.forEach((card) => {
          card.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleCardClick(card);
          });
        });

        // Click anywhere in the block (except on a non-front card) to advance
        this.addEventListener('click', (e) => {
          const clickedCard = e.target && e.target.closest && e.target.closest('.ct-card');
          if (!clickedCard) {
            this.advanceToNextCard();
          }
        });
      }

      handleCardClick(clickedCard) {
        if (this.isAnimating) return;
        if (clickedCard === this.currentFrontCard) {
          // Clicking current card advances to next card (more intuitive)
          this.advanceToNextCard();
          return;
        }
        this.isAnimating = true;

        // Clear existing intervals and timeouts
        clearInterval(this.animationInterval);
        clearTimeout(this.pauseTimeout);

        const centerX = Math.round((this.offsetWidth - this.cardWidth) / 2);
        const centerY = Math.round((this.offsetHeight - this.cardHeight) / 2);

        // Store the previous front card
        const previousFrontCard = this.currentFrontCard;

        // Update currentFrontCard to clickedCard before animations
        this.currentFrontCard = clickedCard;

        // Flip the clicked card to front and center it
        this.setDebug('click', 'start');
        // Compute a crisp scale that does not exceed the image's pixel budget on this DPR
        const clickScale = this.computeClickScaleForCard(clickedCard);
        // Hint browser to fetch higher-res for zoomed display
        const zoomCssWidth = Math.round(this.cardWidth * clickScale);
        this.updateImageSizesForCard(clickedCard, zoomCssWidth);
        const isCovered = this.isCoveredByOtherCards(clickedCard, previousFrontCard);
        const raiseAt = isCovered ? this.d(0.15) : 0;
        // Lower previous immediately so the clicked card can come above when needed
        if (previousFrontCard && previousFrontCard !== clickedCard) {
          gsap.set(previousFrontCard, { zIndex: 999 });
        }

        const tl = gsap.timeline({ onComplete: () => {
          this.isAnimating = false;
          this.setDebug('click', 'done');
        }});

        tl.set(clickedCard, { zIndex: 1001 }, raiseAt);

        tl.to(clickedCard, {
          rotationY: 0,
          x: centerX,
          y: centerY,
          rotation: 0,
          duration: this.d(0.5),
          scale: clickScale
        }, 0);

        tl.add(() => {
          // Scatter other cards after click animation completes
          this.scatterAnimation();
          const pause = this.isMobileViewport() ? 5000 * 0.65 : 5000;
          this.pauseTimeout = setTimeout(() => {
            this.startAnimationCycle();
          }, this.ms(pause));
        });

        // Flip the previous front card face down
        if (previousFrontCard && previousFrontCard !== clickedCard) {
          gsap.to(previousFrontCard, {
            rotationY: 180,
            zIndex: 0,
            scale: 1,
            duration: 0.5
          });
        }
      }

      advanceToNextCard() {
        if (this.isAnimating) return;
        this.isAnimating = true;

        clearInterval(this.animationInterval);
        clearTimeout(this.pauseTimeout);

        const tl = gsap.timeline({ onComplete: () => { this.isAnimating = false; } });
        // Flip a new card to front and center at scale 1
        tl.add(this.flipNewCardToFront());
        // Scatter the rest
        const anim = this.scatterAnimation();
        if (anim) tl.add(anim, '>-0.1');

        // Resume automatic cycle after a pause
        const pause = this.isMobileViewport() ? 5000 * 0.65 : 5000;
        this.pauseTimeout = setTimeout(() => {
          this.startAnimationCycle();
        }, this.ms(pause));
      }

      updateImageSizesForCard(card, targetCssWidth) {
        try {
          const imgs = card ? card.querySelectorAll('img') : [];
          const target = Math.max(1, Math.round(targetCssWidth || this.cardWidth));
          imgs.forEach((img) => {
            if (!img) return;
            img.sizes = `${target}px`;
            // Nudge browsers to re-evaluate candidates
            if (img.srcset) {
              img.srcset = img.srcset;
            }
          });
        } catch (e) {
          // no-op
        }
      }

      hideHeader() {
        if (this.headerEl && !this.headerFaded) {
          this.headerFaded = true;
          gsap.to(this.headerEl, {
            autoAlpha: 0,
            duration: this.d(0.3),
            ease: 'power1.out'
          });
        }
      }

      startAnimationCycle() {
        const animations = [
          this.scatterAnimation.bind(this),
          this.verticalWipeAnimation.bind(this),
          this.stackAnimation.bind(this)
        ];

        const flipAndAnimate = () => {
          if (this.isAnimating) return;

          this.isAnimating = true;
          const tl = gsap.timeline({ onStart: () => this.setDebug('cycle', 'start'), onComplete: () => { this.isAnimating = false; this.setDebug('cycle', 'done'); } });
          tl.add(this.flipNewCardToFront());

          const randomAnimation = animations[Math.floor(Math.random() * animations.length)];
          const anim = randomAnimation();
          if (anim) tl.add(anim, '>-0.1');
        };

        // Clear existing interval
        clearInterval(this.animationInterval);

        // Set new interval to flip and animate cards
        this.animationInterval = setInterval(flipAndAnimate, this.ms(5000));
      }

      animateCards() {
        // Initial animation
        this.scatterAnimation();

        // Start the animation cycle
        this.startAnimationCycle();
      }

      handleResize() {
        this.updateCardDimensions();
        const centerX = Math.round((this.offsetWidth - this.cardWidth) / 2);
        const centerY = Math.round((this.offsetHeight - this.cardHeight) / 2);
        if (this.currentFrontCard) {
          gsap.set(this.currentFrontCard, { x: centerX, y: centerY, scale: this.getFrontScale() });
        }
      }

      updateCardDimensions() {
        const sample = this.cards && this.cards[0];
        if (!sample) return;
        const rect = sample.getBoundingClientRect();
        if (rect.width && rect.height) {
          this.cardWidth = rect.width;
          this.cardHeight = rect.height;
        }
      }

      // Determine if the given card is meaningfully covered by any other non-front card
      isCoveredByOtherCards(card, ignoreCard) {
        if (!card) return false;
        const a = card.getBoundingClientRect();
        for (const other of this.cards) {
          if (other === card || other === ignoreCard) continue;
          const b = other.getBoundingClientRect();
          const overlap = this.overlapRatio(a, b);
          if (overlap > this.coverOverlapThreshold) return true;
        }
        return false;
      }

      overlapRatio(a, b) {
        const xOverlap = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
        const yOverlap = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
        const overlapArea = xOverlap * yOverlap;
        const aArea = a.width * a.height || 1;
        return overlapArea / aArea;
      }

      // ----- Debug and speed helpers -----
      initializeFlagsFromQuery() {
        try {
          const params = new URLSearchParams(window.location.search);
          const q = params.get('ctcards_debug');
          const qSpeed = params.get('ctcards_speed');
          const globalDebug = typeof window !== 'undefined' && window.CT_CARDS_DEBUG === true;
          this.debug = globalDebug || q === '1' || q === 'true';
          if (this.debug) {
            this.speed = 0.25;
          }
          if (qSpeed && !Number.isNaN(parseFloat(qSpeed))) {
            this.speed = parseFloat(qSpeed);
          }
          if (!this.speed || this.speed <= 0) this.speed = 1;
        } catch (e) {
          this.debug = false;
          this.speed = 1;
        }
      }

      d(seconds) {
        return seconds / this.speed;
      }

      ms(milliseconds) {
        return Math.round(milliseconds / this.speed);
      }

      // Mobile helpers
      isMobileViewport() {
        return typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches;
      }

      getMobileScaleFactor() {
        return this.isMobileViewport() ? 0.65 : 1;
      }

      getFrontScale() {
        return 1;
      }

      getClickScale() {
        return 1.6 * this.getMobileScaleFactor();
      }

      computeClickScaleForCard(card) {
        // Desired visual scale
        const desired = this.getClickScale();
        try {
          const img = card && (card.querySelector('.ct-card__front img') || card.querySelector('img'));
          const natural = img && img.naturalWidth ? img.naturalWidth : this.cardWidth;
          const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
          const maxScale = natural / (this.cardWidth * dpr);
          const safe = Math.max(1, Math.min(desired, maxScale));
          return safe;
        } catch (e) {
          return Math.max(1, desired);
        }
      }

      createDebugOverlay() {
        const el = document.createElement('div');
        el.className = 'ct-cards-debug';
        el.setAttribute('aria-live', 'polite');
        this.appendChild(el);
        this.debugEl = el;
        this.updateDebug();
      }

      setDebug(animation, stage) {
        if (!this.debug) return;
        this.debugState = { animation, stage };
        this.updateDebug();
      }

      updateDebug() {
        if (!this.debug || !this.debugEl) return;
        const frontIdx = this.currentFrontCard ? this.cards.indexOf(this.currentFrontCard) : -1;
        const lines = [
          `speed: ${this.speed}`,
          `isAnimating: ${this.isAnimating}`,
          `animation: ${this.debugState.animation}`,
          `stage: ${this.debugState.stage}`,
          `frontIndex: ${frontIdx}`,
          `cardIndex(next): ${this.cardIndex}`
        ];
        this.debugEl.textContent = lines.join(' \n ');
      }
    }
  );
}
